import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import configService from '../services/configService';
import { useToast } from './Toast';

function getFlagEmoji(countryCode) {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

function BusinessInfo({ onComplete, cachedBusinessConfig, adminBusinessId }) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    businessName: '',
    address: '',
    website: '',
    phoneNumber: '',
    businessHours: ''
  });
  const [countryCode, setCountryCode] = useState('+1');
  const [loading, setLoading] = useState(!cachedBusinessConfig);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const effectiveUserId = adminBusinessId || user?.attributes?.sub;

  useEffect(() => {
    if (cachedBusinessConfig) {
      console.log('[BusinessInfo] Using cached config:', {
        business_name: cachedBusinessConfig.business_name,
        cognito_user_id: cachedBusinessConfig.cognito_user_id,
        isAdminMode: !!adminBusinessId,
        WARNING: adminBusinessId && cachedBusinessConfig.cognito_user_id !== adminBusinessId ? 'CACHE MISMATCH - cached config is for different user!' : 'OK'
      });
      // Extract country code and format phone number for display
      let displayPhone = '';
      let extractedCountryCode = '+1';
      
      if (cachedBusinessConfig.phone_number) {
        const allDigits = cachedBusinessConfig.phone_number.replace(/\D/g, '');
        const countryCodeMap = {
          '1': { code: '+1', length: 1 },
          '44': { code: '+44', length: 2 },
          '33': { code: '+33', length: 2 },
          '49': { code: '+49', length: 2 },
          '39': { code: '+39', length: 2 },
          '34': { code: '+34', length: 2 },
          '61': { code: '+61', length: 2 },
          '81': { code: '+81', length: 2 }
        };
        
        for (const [digits, info] of Object.entries(countryCodeMap)) {
          if (allDigits.startsWith(digits)) {
            extractedCountryCode = info.code;
            displayPhone = allDigits.slice(info.length);
            if (info.code === '+1' && displayPhone.length === 10) {
              displayPhone = `${displayPhone.slice(0, 3)}-${displayPhone.slice(3, 6)}-${displayPhone.slice(6)}`;
            }
            break;
          }
        }
        
        if (!displayPhone && allDigits.length === 10) {
          displayPhone = `${allDigits.slice(0, 3)}-${allDigits.slice(3, 6)}-${allDigits.slice(6)}`;
        }
      }
      
      setCountryCode(extractedCountryCode);
      setFormData({
        businessName: cachedBusinessConfig.business_name || '',
        address: cachedBusinessConfig.address || '',
        website: cachedBusinessConfig.website || '',
        phoneNumber: displayPhone,
        businessHours: cachedBusinessConfig.business_hours || ''
      });
      setLoading(false);
    } else {
      loadBusinessInfo();
    }
  }, [cachedBusinessConfig]);

  // Expose save function globally for Setup.js
  useEffect(() => {
    window.saveBusinessInfo = handleSave;
    return () => {
      delete window.saveBusinessInfo;
    };
  }, [formData]);

  const loadBusinessInfo = async () => {
    try {
      const cognitoUserId = effectiveUserId;
      if (!cognitoUserId) {
        setLoading(false);
        return;
      }

      console.log('[BusinessInfo] Loading config:', {
        cognitoUserId,
        isAdminMode: !!adminBusinessId,
        willUseAdminEndpoint: !!adminBusinessId
      });

      // Use admin endpoint if adminBusinessId is provided
      const config = adminBusinessId
        ? await configService.getAdminUserConfig(cognitoUserId)
        : await configService.getBusinessConfigByUserId(cognitoUserId);
      
      console.log('[BusinessInfo] Loaded config:', {
        business_name: config.business_name,
        cognito_user_id: config.cognito_user_id,
        usedAdminEndpoint: !!adminBusinessId
      });
      
      if (config) {
        // Extract country code and format phone number for display
        let displayPhone = '';
        let extractedCountryCode = '+1';
        
        if (config.phone_number) {
          const allDigits = config.phone_number.replace(/\D/g, '');
          const countryCodeMap = {
            '1': { code: '+1', length: 1 },
            '44': { code: '+44', length: 2 },
            '33': { code: '+33', length: 2 },
            '49': { code: '+49', length: 2 },
            '39': { code: '+39', length: 2 },
            '34': { code: '+34', length: 2 },
            '61': { code: '+61', length: 2 },
            '81': { code: '+81', length: 2 }
          };
          
          for (const [digits, info] of Object.entries(countryCodeMap)) {
            if (allDigits.startsWith(digits)) {
              extractedCountryCode = info.code;
              displayPhone = allDigits.slice(info.length);
              if (info.code === '+1' && displayPhone.length === 10) {
                displayPhone = `${displayPhone.slice(0, 3)}-${displayPhone.slice(3, 6)}-${displayPhone.slice(6)}`;
              }
              break;
            }
          }
          
          if (!displayPhone && allDigits.length === 10) {
            displayPhone = `${allDigits.slice(0, 3)}-${allDigits.slice(3, 6)}-${allDigits.slice(6)}`;
          }
        }
        
        setCountryCode(extractedCountryCode);
        setFormData({
          businessName: config.business_name || '',
          address: config.address || '',
          website: config.website || '',
          phoneNumber: displayPhone,
          businessHours: config.business_hours || ''
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to load business info:', error);
      setError('Failed to load business information.');
      setLoading(false);
    }
  };

  const handleSave = async () => {
    console.log('[BusinessInfo] handleSave called:', {
      formData,
      effectiveUserId,
      isAdminMode: !!adminBusinessId
    });
    setSaving(true);
    setError('');

    try {
      const cognitoUserId = effectiveUserId;
      if (!cognitoUserId) {
        throw new Error('User not authenticated');
      }

      // Validate required fields
      if (!formData.businessName.trim()) {
        throw new Error('Business name is required');
      }
      if (!formData.phoneNumber.trim()) {
        throw new Error('Phone number is required');
      }
      if (!formData.website.trim()) {
        throw new Error('Website is required');
      }
      if (!formData.address.trim()) {
        throw new Error('Address is required');
      }

      const dataToSave = {
        business_name: formData.businessName,
        address: formData.address,
        website: formData.website,
        phone_number: formData.phoneNumber ? `${countryCode}${formData.phoneNumber.replace(/\D/g, '')}` : '',
        business_hours: formData.businessHours
      };
      
      console.log('[BusinessInfo] Saving data:', {
        dataToSave,
        cognitoUserId,
        isAdminMode: !!adminBusinessId
      });
      
      const result = adminBusinessId
        ? await configService.updateAdminUserConfig(cognitoUserId, dataToSave)
        : await configService.updateBusinessConfig(cognitoUserId, dataToSave);
      
      console.log('[BusinessInfo] Save result:', result);
      
      // Verify the data was saved by fetching it back
      const verifyConfig = adminBusinessId
        ? await configService.getAdminUserConfig(cognitoUserId)
        : await configService.getBusinessConfigByUserId(cognitoUserId);
      
      console.log('[BusinessInfo] Verified saved config:', {
        business_name: verifyConfig.business_name,
        phone_number: verifyConfig.phone_number,
        website: verifyConfig.website,
        address: verifyConfig.address
      });

      showToast('Saved successfully!');
      if (onComplete) onComplete();
      return true;
    } catch (error) {
      console.error('Failed to save business info:', error);
      setError(error.message);
      showToast('Failed to save', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 text-sm">Loading business settings...</p>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label className="block mb-2 font-medium text-gray-800 text-sm">
            Business Name *
          </label>
          <input
            type="text"
            value={formData.businessName}
            onChange={(e) => setFormData({...formData, businessName: e.target.value})}
            placeholder="Your Business Name"
            className="w-full border border-gray-300 rounded p-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block mb-2 font-medium text-gray-800 text-sm">
            Phone Number *
          </label>
          <div className="flex gap-2">
            <select 
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="border border-gray-300 rounded p-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-shrink-0"
            >
              <option value="+1">{getFlagEmoji('US')} +1</option>
              <option value="+1">{getFlagEmoji('CA')} +1</option>
              <option value="+44">{getFlagEmoji('GB')} +44</option>
              <option value="+33">{getFlagEmoji('FR')} +33</option>
              <option value="+49">{getFlagEmoji('DE')} +49</option>
              <option value="+39">{getFlagEmoji('IT')} +39</option>
              <option value="+34">{getFlagEmoji('ES')} +34</option>
              <option value="+61">{getFlagEmoji('AU')} +61</option>
              <option value="+81">{getFlagEmoji('JP')} +81</option>
            </select>
            <input
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => {
                const value = e.target.value;
                const digits = value.replace(/\D/g, '');
                
                let formatted = digits.slice(0, 10);
                if (formatted.length >= 6) {
                  formatted = `${formatted.slice(0, 3)}-${formatted.slice(3, 6)}-${formatted.slice(6)}`;
                } else if (formatted.length >= 3) {
                  formatted = `${formatted.slice(0, 3)}-${formatted.slice(3)}`;
                }
                
                setFormData({...formData, phoneNumber: formatted});
              }}
              placeholder="123-456-7890"
              className="flex-1 border border-gray-300 rounded p-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
        </div>

        <div>
          <label className="block mb-2 font-medium text-gray-800 text-sm">
            Address *
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            placeholder="123 Main St, City, State 12345"
            className="w-full border border-gray-300 rounded p-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block mb-2 font-medium text-gray-800 text-sm">
            Website *
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => setFormData({...formData, website: e.target.value})}
            placeholder="https://yourbusiness.com"
            className="w-full border border-gray-300 rounded p-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block mb-2 font-medium text-gray-800 text-sm">
            Business Hours
          </label>
          <textarea
            value={formData.businessHours}
            onChange={(e) => setFormData({...formData, businessHours: e.target.value})}
            placeholder="Mon-Fri: 9:00 AM - 5:00 PM&#10;Sat: 10:00 AM - 2:00 PM&#10;Sun: Closed"
            rows="4"
            className="w-full border border-gray-300 rounded p-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
          <p className="text-[11px] text-gray-600 mt-1">
            Enter your business hours in a readable format. This will be used by your <a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('switchSettingsTab', { detail: 'assistant' })); }} className="text-blue-600 hover:underline">AI assistant</a>.
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 text-white px-6 py-3 rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BusinessInfo;
